import React, { useState, useEffect } from "react";
import { useFormikContext } from "formik";
import { Row, Col, Form } from "react-bootstrap";
import { Autocomplete, Box } from "@mui/material";
import BootstrapTextField from "Common/Components/InputComponent/BootstrapTextField";
import { QuotationFormValues } from "../initialValues";
import { ServiceQuotationFormValues } from "../serviceInitialValues";
import { MecordColors } from "Constants/Color";
import { autocompleteDropdownStyles } from "Constants/Style";
import { Axios } from "api/axios";
import { API_ENDPOINT } from "helpers/Endpoints/endpoints";
import { toast } from "react-toastify";

interface User {
  user_id: string;
  username: string;
  email: string;
  first_name: string;
  last_name: string;
}

const getInitials = (firstName: string, lastName: string): string => {
  const first = firstName?.charAt(0) || "";
  const last = lastName?.charAt(0) || "";
  return (first + last).toUpperCase();
};

const AssignMembersSection: React.FC = () => {
  const { values, setFieldValue } = useFormikContext<
    QuotationFormValues | ServiceQuotationFormValues
  >();
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState<boolean>(true);

  // Derive from form so it stays in sync when members are set from enquiry or add/remove
  const selectedMemberIds = values.members.map((m) => m.id);

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        setLoading(true);
        const response = await Axios.get(
          API_ENDPOINT.USER_MANAGEMENT.USER.DROPDOWN
        );
        if (response.data.success) {
          setUsers(response.data.data);
        }
      } catch (error) {
        console.error("Error fetching users:", error);
        toast.error("Failed to fetch users");
      } finally {
        setLoading(false);
      }
    };

    fetchUsers();
  }, []);

  const handleMemberAdd = (user: User) => {
    if (!selectedMemberIds.includes(user.user_id)) {
      const newMember = {
        id: user.user_id,
        name: `${user.first_name} ${user.last_name}`.trim(),
        initials: getInitials(user.first_name, user.last_name),
        first_name: user.first_name || "",
        last_name: user.last_name || "",
      };
      setFieldValue("members", [...values.members, newMember]);
    }
  };

  const handleMemberRemove = (memberId: string) => {
    setFieldValue(
      "members",
      values.members.filter((m) => m.id !== memberId)
    );
  };

  const availableMembers = users.filter(
    (user) => !selectedMemberIds.includes(user.user_id)
  );
 console.log("values members in assign members section", values.members);
  const disabled = Boolean((values as any).has_order || (values as any).sales_order_service_id);

  return (
    <div className="rounded mb-3">
      <div className="d-flex justify-content-between align-items-center mb-3 gap-1">
        <h5 className="fw-semibold">Add Members</h5>
      </div>

      <Row
        className="g-3 pb-3 px-1 rounded mb-2"
        style={{ backgroundColor: MecordColors?.LightThemeBGColor }}
      >
        <Col xs={12} md={12}> 
          <Form.Group>
            <Form.Label>
              Assign Members
              {/* <span className="text-danger">*</span> */}
            </Form.Label>
            <Autocomplete
              options={availableMembers}
              loading={loading}
              disabled={disabled}
              getOptionLabel={(option) =>
                `${option.first_name} ${option.last_name}` || ""
              }
              isOptionEqualToValue={(option, value) =>
                option.user_id === value.user_id
              }
              onChange={(_: any, newValue: User | null) => {
                if (newValue) {
                  handleMemberAdd(newValue);
                }
              }}
              ListboxProps={autocompleteDropdownStyles.ListboxProps}
              renderOption={(props, option, { selected }) => (
                <li {...props} style={{ padding: 0 }}>
                  <Box
                    sx={{
                      px: 1.5,
                      py: 0.8,
                      width: "100%",
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      cursor: "pointer",
                      "&:hover": {
                        backgroundColor: MecordColors.Hover,
                        color: MecordColors.ActiveText,
                      },
                      ...(selected && {
                        backgroundColor: MecordColors.ActiveBackground,
                        color: MecordColors.ActiveText,
                      }),
                    }}
                  >
                    <span>{`${option.first_name} ${option.last_name}`}</span>
                  </Box>
                </li>
              )}
              renderInput={(params) => (
                <BootstrapTextField
                  {...params}
                  placeholder="Search User"
                  size="small"
                  sx={autocompleteDropdownStyles.inputSx}
                />
              )}
            />
          </Form.Group>
        </Col>
      </Row>

      <Row className="g-3 rounded">
        {values.members.length > 0 && (
          <Col xs={12} md={12}>
            <div className="d-flex flex-wrap gap-2">
              {values.members.map((member) => (
                <div
                  key={member.id}
                  className="d-flex align-items-center px-2 py-1 rounded gap-2"
                  style={{
                    background: MecordColors?.LightThemeBGColor,
                    border: `1px solid ${MecordColors?.ThemeBGColor}`,
                    fontSize: "14px",
                  }}
                >
                  <span
                    className="rounded-circle d-flex align-items-center justify-content-center"
                    style={{
                      width: "24px",
                      height: "24px",
                      fontSize: "0.75rem",
                      fontWeight: "bold",
                      backgroundColor: "#fff",
                      color: MecordColors.ThemeBGColor,
                    }}
                  >
                    {member.initials}
                  </span>
                  {member.name}
                  {!disabled && (
                    <span
                      style={{
                        marginLeft: "4px",
                        cursor: "pointer",
                        color: "red",
                      }}
                      onClick={() => handleMemberRemove(member.id)}
                    >
                      ✕
                    </span>
                  )}
                </div>
              ))}
            </div>
          </Col>
        )}
      </Row>
    </div>
  );
};

export default AssignMembersSection;
